import React from 'react';
import { Button, ButtonGroup } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { Sr89DebugResults } from './Sr89DebugPage';

interface ExportDebugDataProps {
  results: Sr89DebugResults;
  calculationId: string;
}

const ExportDebugData: React.FC<ExportDebugDataProps> = ({ results, calculationId }) => {
  
  const exportToJSON = () => {
    const dataStr = JSON.stringify(results, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `sr89_debug_${calculationId}_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const exportToCSV = () => {
    const csvRows: Array<Array<string | number>> = [];
    csvRows.push(['Categoría', 'Variable', 'Valor', 'Fórmula']);
    
    // Eficiencias
    csvRows.push(['Eficiencias', 'Eficiencia Sr-89', results.efficiencySr89, 'x4*sqp⁴ + x3*sqp³ + x2*sqp² + x*sqp + a']);
    csvRows.push(['Eficiencias', 'Eficiencia Sr-90', results.efficiencySr90, 'x4*sqp⁴ + x3*sqp³ + x2*sqp² + x*sqp + a']);
    csvRows.push(['Eficiencias', 'Eficiencia Y-90', results.efficiencyY90, 'x4*sqp⁴ + x3*sqp³ + x2*sqp² + x*sqp + a']);
    
    // Datos de contaje
    csvRows.push(['Contaje', 'CPM Fondo', results.cpmFondo, '']);
    csvRows.push(['Contaje', 'Tiempo Total Contaje (min)', results.countingTime, '']);
    csvRows.push(['Contaje', 'Error CPM 1er Contaje', results.errorCpmContaje1, 'cpm1/time1 + cpmFondo/timeFondo']);
    csvRows.push(['Contaje', 'Error CPM 2do Contaje', results.errorCpmContaje2, 'cpm2/time2 + cpmFondo/timeFondo']);
    
    // Constantes
    csvRows.push(['Constantes', 'λ Sr-89', results.cteDisintegrationSr89, '']);
    csvRows.push(['Constantes', 'λ Sr-90', results.cteDisintegrationSr90, '']);
    csvRows.push(['Constantes', 'λ Y-90', results.cteDisintegrationY90, '']);
    
    // Tiempos
    csvRows.push(['Tiempos', 't1 (min)', results.t1, '']);
    csvRows.push(['Tiempos', 't2 (min)', results.t2, '']);
    csvRows.push(['Tiempos', 'Tiempo Final Precipitación (min)', results.calculationPrecipitationFinalTime, '']);
    csvRows.push(['Tiempos', 'Tiempo Muestreo (min)', results.calculationSamplingTimeMin, '']);
    
    // CPM
    csvRows.push(['CPM', 'CPM 1er Contaje', results.cpm1Contaje, '']);
    csvRows.push(['CPM', 'CPM 2do Contaje', results.cpm2Contaje, '']);
    csvRows.push(['CPM', 'CPM Neto 1', results.cpmNeto1, 'cpm1 - cpm_fondo']);
    csvRows.push(['CPM', 'CPM Neto 2', results.cpmNeto2, 'cpm2 - cpm_fondo']);
    
    // Factores
    csvRows.push(['Factores', 'Factor b', results.factorB, 'exp(-λ_y90 * t2)']);
    csvRows.push(['Factores', 'Factor c', results.factorC, 'exp(-λ_y90 * t1)']);
    csvRows.push(['Factores', 'Factor d', results.factorD, 'exp(-λ_sr89 * t1)']);
    csvRows.push(['Factores', 'Factor e', results.factorE, 'exp(-λ_sr89 * t2)']);
    csvRows.push(['Factores', 'Factor m', results.factorM, 'λ_y90 / (λ_y90 - λ_sr90)']);
    csvRows.push(['Factores', 'Factor n', results.factorN, 'eff_y90 / eff_sr90']);
    
    // CPM0 Sr89
    csvRows.push(['CPM0 Sr-89', 'Término 1 Numerador', results.term1Numerator, 'cpmNeto1 * (1 + m*n*(1-b))']);
    csvRows.push(['CPM0 Sr-89', 'Término 2 Numerador', results.term2Numerator, 'cpmNeto2 * (1 + m*n*(1-c))']);
    csvRows.push(['CPM0 Sr-89', 'Numerador', results.numeratorCpm0Sr89, 'term1 - term2']);
    csvRows.push(['CPM0 Sr-89', 'Término 1 Denominador', results.term1Denominator, 'd * (1 + m*n*(1-b))']);
    csvRows.push(['CPM0 Sr-89', 'Término 2 Denominador', results.term2Denominator, 'e * (1 + m*n*(1-c))']);
    csvRows.push(['CPM0 Sr-89', 'Denominador', results.denominatorCpm0Sr89, 'term1 - term2']);
    csvRows.push(['CPM0 Sr-89', 'CPM0 Sr-89', results.cpm0Sr89, 'numerador / denominador']);
    csvRows.push(['CPM0 Sr-89', 'Error CPM0 Sr-89', results.errorCpm0Sr89, 'sqrt((...)²+(...²)) / denominador']);
    
    // Actividad
    csvRows.push(['Actividad', 'Cantidad Muestra', results.sampleQuantity, '']);
    csvRows.push(['Actividad', 'exp(λ*t_final)', results.expValueActivity, 'e^(λ_sr89 * t_final)']);
    csvRows.push(['Actividad', 'exp(-λ*t_muestreo)', results.expValue2Activity, 'e^(-λ_sr89 * t_muestreo)']);
    csvRows.push(['Actividad', 'Eficiencia Sr-89', results.efficiencySr89Value, '']);
    csvRows.push(['Actividad', 'Resultado AA', results.resultToCalculation, '']);
    csvRows.push(['Actividad', 'Dividendo', results.dividendoActivity, 'cpm0*expVal*λ*t_muestreo']);
    csvRows.push(['Actividad', 'Divisor', results.divisorActivity, '60*eff*cant*AA*(1-expVal2)']);
    
    // LID
    csvRows.push(['LID', 'sqrt(cpmFondo*tiempo)', results.sqrtCpmFondoTime, '']);
    csvRows.push(['LID', 'Numerador', results.lidNumerator, '4.66*sqrt(...)/tiempo*...']);
    csvRows.push(['LID', 'Denominador', results.lidDenominator, '60*eff*cant*AA*(1-expVal2)']);
    
    // Incertidumbre
    csvRows.push(['Incertidumbre', 'Numerador', results.uncertaintyNumerator, '2*error*expVal*λ*t_muestreo']);
    csvRows.push(['Incertidumbre', 'Denominador', results.uncertaintyDenominator, '60*eff*cant*AA*(1-expVal2)']);
    
    // Resultados finales
    csvRows.push(['RESULTADOS', 'ACTIVIDAD (Bq/m³)', results.activity, '']);
    csvRows.push(['RESULTADOS', 'INCERTIDUMBRE', results.uncertainty, '']);
    csvRows.push(['RESULTADOS', 'LID', results.lid, '']);
    
    const csvContent = csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const dataUri = 'data:text/csv;charset=utf-8,\uFEFF' + encodeURIComponent(csvContent);
    
    const exportFileDefaultName = `sr89_debug_${calculationId}_${new Date().toISOString().split('T')[0]}.csv`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const exportToText = () => {
    let textContent = `DEPURACIÓN DE CÁLCULOS Sr-89 (Método Currie)\n`;
    textContent += `Calculation ID: ${calculationId}\n`;
    textContent += `Fecha de exportación: ${new Date().toLocaleString()}\n`;
    textContent += `${'='.repeat(80)}\n\n`;
    
    textContent += `1. EFICIENCIAS\n${'-'.repeat(80)}\n`;
    textContent += `Eficiencia Sr-89: ${results.efficiencySr89.toExponential(6)}\n`;
    textContent += `Eficiencia Sr-90: ${results.efficiencySr90.toExponential(6)}\n`;
    textContent += `Eficiencia Y-90:  ${results.efficiencyY90.toExponential(6)}\n\n`;
    
    textContent += `2. DATOS DE CONTAJE Y FONDO\n${'-'.repeat(80)}\n`;
    textContent += `CPM Fondo: ${results.cpmFondo.toExponential(6)}\n`;
    textContent += `Tiempo Total Contaje Fondos (min): ${results.countingTime}\n`;
    textContent += `Error CPM 1er Contaje: ${results.errorCpmContaje1.toExponential(6)}\n`;
    textContent += `Error CPM 2do Contaje: ${results.errorCpmContaje2.toExponential(6)}\n\n`;
    
    textContent += `3. CONSTANTES DE DESINTEGRACIÓN\n${'-'.repeat(80)}\n`;
    textContent += `λ Sr-89: ${results.cteDisintegrationSr89.toExponential(6)}\n`;
    textContent += `λ Sr-90: ${results.cteDisintegrationSr90.toExponential(6)}\n`;
    textContent += `λ Y-90:  ${results.cteDisintegrationY90.toExponential(6)}\n\n`;
    
    textContent += `4. TIEMPOS\n${'-'.repeat(80)}\n`;
    textContent += `t1 (Primer Contaje, min): ${results.t1}\n`;
    textContent += `t2 (Segundo Contaje, min): ${results.t2}\n`;
    textContent += `Tiempo Final Precipitación (min): ${results.calculationPrecipitationFinalTime}\n`;
    textContent += `Tiempo Muestreo (min): ${results.calculationSamplingTimeMin}\n\n`;
    
    textContent += `5. CPM DE CONTAJES\n${'-'.repeat(80)}\n`;
    textContent += `CPM 1er Contaje: ${results.cpm1Contaje.toExponential(6)}\n`;
    textContent += `CPM 2do Contaje: ${results.cpm2Contaje.toExponential(6)}\n`;
    textContent += `CPM Neto 1: ${results.cpmNeto1.toExponential(6)}\n`;
    textContent += `CPM Neto 2: ${results.cpmNeto2.toExponential(6)}\n\n`;
    
    textContent += `6. FACTORES EXPONENCIALES Y AUXILIARES\n${'-'.repeat(80)}\n`;
    textContent += `Factor b (exp(-λ_y90*t2)): ${results.factorB.toExponential(6)}\n`;
    textContent += `Factor c (exp(-λ_y90*t1)): ${results.factorC.toExponential(6)}\n`;
    textContent += `Factor d (exp(-λ_sr89*t1)): ${results.factorD.toExponential(6)}\n`;
    textContent += `Factor e (exp(-λ_sr89*t2)): ${results.factorE.toExponential(6)}\n`;
    textContent += `Factor m: ${results.factorM.toExponential(6)}\n`;
    textContent += `Factor n: ${results.factorN.toExponential(6)}\n\n`;
    
    textContent += `7. CÁLCULO DE CPM₀ Sr-89\n${'-'.repeat(80)}\n`;
    textContent += `Término 1 Numerador: ${results.term1Numerator.toExponential(6)}\n`;
    textContent += `Término 2 Numerador: ${results.term2Numerator.toExponential(6)}\n`;
    textContent += `Numerador CPM₀ Sr-89: ${results.numeratorCpm0Sr89.toExponential(6)}\n`;
    textContent += `Término 1 Denominador: ${results.term1Denominator.toExponential(6)}\n`;
    textContent += `Término 2 Denominador: ${results.term2Denominator.toExponential(6)}\n`;
    textContent += `Denominador CPM₀ Sr-89: ${results.denominatorCpm0Sr89.toExponential(6)}\n`;
    textContent += `CPM₀ Sr-89: ${results.cpm0Sr89.toExponential(6)}\n`;
    textContent += `Error CPM₀ Sr-89: ${results.errorCpm0Sr89.toExponential(6)}\n\n`;
    
    textContent += `8. COMPONENTES DEL CÁLCULO DE ACTIVIDAD\n${'-'.repeat(80)}\n`;
    textContent += `Cantidad Muestra: ${results.sampleQuantity}\n`;
    textContent += `exp(λ_sr89*t_final): ${results.expValueActivity.toExponential(6)}\n`;
    textContent += `exp(-λ_sr89*t_muestreo): ${results.expValue2Activity.toExponential(6)}\n`;
    textContent += `Eficiencia Sr-89: ${results.efficiencySr89Value.toExponential(6)}\n`;
    textContent += `Resultado AA: ${results.resultToCalculation}\n`;
    textContent += `Dividendo: ${results.dividendoActivity.toExponential(6)}\n`;
    textContent += `Divisor: ${results.divisorActivity.toExponential(6)}\n\n`;
    
    textContent += `9. COMPONENTES DEL CÁLCULO DE LID\n${'-'.repeat(80)}\n`;
    textContent += `sqrt(cpmFondo*tiempo): ${results.sqrtCpmFondoTime.toExponential(6)}\n`;
    textContent += `Numerador LID: ${results.lidNumerator.toExponential(6)}\n`;
    textContent += `Denominador LID: ${results.lidDenominator.toExponential(6)}\n\n`;
    
    textContent += `10. COMPONENTES DEL CÁLCULO DE INCERTIDUMBRE\n${'-'.repeat(80)}\n`;
    textContent += `Numerador: ${results.uncertaintyNumerator.toExponential(6)}\n`;
    textContent += `Denominador: ${results.uncertaintyDenominator.toExponential(6)}\n\n`;
    
    textContent += `${'='.repeat(80)}\n`;
    textContent += `RESULTADOS FINALES\n`;
    textContent += `${'='.repeat(80)}\n`;
    textContent += `🎯 ACTIVIDAD (Bq/m³): ${results.activity.toExponential(6)}\n`;
    textContent += `📊 INCERTIDUMBRE: ${results.uncertainty.toExponential(6)}\n`;
    textContent += `🔍 LID (Límite de Detección): ${results.lid.toExponential(6)}\n`;
    textContent += `${'='.repeat(80)}\n`;
    
    const dataUri = 'data:text/plain;charset=utf-8,' + encodeURIComponent(textContent);
    const exportFileDefaultName = `sr89_debug_${calculationId}_${new Date().toISOString().split('T')[0]}.txt`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <ButtonGroup variant="outlined" sx={{ mb: 2 }}>
      <Button startIcon={<DownloadIcon />} onClick={exportToJSON}>
        Exportar JSON
      </Button>
      <Button startIcon={<DownloadIcon />} onClick={exportToCSV}>
        Exportar CSV
      </Button>
      <Button startIcon={<DownloadIcon />} onClick={exportToText}>
        Exportar TXT
      </Button>
    </ButtonGroup>
  );
};

export default ExportDebugData;
